import {
  AppstoreOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CrownOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  FlagOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Skeleton, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { DATE_FORMATS, GENDERS } from '../../../../../../common/constants';
import { formatDate, getPlayPlanLabel } from '../../../../../../common/utils';
import {
  getChildAgeLabel,
  getFullName,
  getInitials,
  getWeekProgress,
} from '../../../../ultraPlanUtils';
import { openRoadmapDocument } from '../subscriber/SubscriberOverview';

const { Text } = Typography;

const getValidityLabel = (expireAt) => {
  if (!expireAt) return null;
  const date = formatDate(expireAt, DATE_FORMATS?.REGULAR);
  const daysLeft = dayjs(expireAt)?.diff(dayjs(), 'day');
  if (daysLeft < 0) return { text: `Expired on ${date}`, isExpired: true };
  return {
    text: `Valid till ${date} · ${daysLeft} day${
      daysLeft === 1 ? '' : 's'
    } left`,
    isExpired: false,
  };
};

const StatTile = ({ icon, label, value, suffix, tone, children }) => (
  <div className={`roadmap-stat ${tone ? `is-${tone}` : ''}`}>
    <span className="roadmap-stat-icon">{icon}</span>
    <div className="roadmap-stat-body">
      <Text type="secondary" className="roadmap-stat-label">
        {label}
      </Text>
      <div className="roadmap-stat-value">
        {value}
        {suffix && <span className="roadmap-stat-suffix">{suffix}</span>}
      </div>
      {children}
    </div>
  </div>
);

const getRoadmapStatus = ({ incompleteWeeks, plannedWeeks, planWeeks }) => {
  if (incompleteWeeks) {
    return {
      icon: <ExclamationCircleFilled />,
      value: `${incompleteWeeks} week${
        incompleteWeeks > 1 ? 's have' : ' has'
      } empty slots`,
      tone: 'warning',
    };
  }
  const weeksLeft = planWeeks ? planWeeks - plannedWeeks : 0;
  if (weeksLeft > 0) {
    return {
      icon: <ClockCircleOutlined />,
      value: `${weeksLeft} week${weeksLeft > 1 ? 's' : ''} left to plan`,
      tone: 'info',
    };
  }
  return {
    icon: <CheckCircleFilled />,
    value: planWeeks ? 'Fully planned' : 'All planned weeks filled',
    tone: 'success',
  };
};

const RoadmapContextCard = ({
  subscription,
  defaultChild,
  isChildLoading,
  hasChildError,
  roadmap,
  weeks = [],
  onOpenSubscriber,
}) => {
  const ageLabel = getChildAgeLabel(defaultChild?.dateOfBirth);
  const genderLabel = GENDERS?.find(
    (gender) => gender?.value === defaultChild?.gender,
  )?.label;
  const assignedCount = weeks?.reduce(
    (total, week) => total + getWeekProgress(week)?.assignedCount,
    0,
  );
  const planWeeks = subscription?.planDurationWeeks;
  const toysPerWeek = parseInt(
    subscription?.subscriptionFeatures?.totalKeepCountPerMonth,
    10,
  );
  const totalSlots =
    planWeeks && toysPerWeek > 0
      ? planWeeks * toysPerWeek
      : weeks?.reduce(
          (total, week) => total + getWeekProgress(week)?.totalCount,
          0,
        );
  const incompleteWeeks = weeks?.filter(
    (week) => !getWeekProgress(week)?.isComplete,
  )?.length;
  const currentWeekNumber =
    roadmap?.currentWeekNumber ||
    weeks?.find((week) => week?.isCurrentWeek)?.weekNumber;
  const roadmapStatus = getRoadmapStatus({
    incompleteWeeks,
    plannedWeeks: weeks?.length,
    planWeeks,
  });
  const roadmapDocument = subscription?.metaData?.ultraPlanRoadMap;
  const planLabel =
    (subscription?.subscriptionPlan &&
      getPlayPlanLabel(subscription?.subscriptionPlan)) ||
    '-';
  const validity = getValidityLabel(subscription?.expireAt);
  let childSummary = {
    name: 'No default child',
    details: 'Set one in Manage Children',
  };
  if (defaultChild) {
    childSummary = {
      name: getFullName(defaultChild),
      details:
        [ageLabel, genderLabel]?.filter(Boolean)?.join(' · ') ||
        'Age not added',
    };
  } else if (hasChildError) {
    childSummary = {
      name: 'Children not loaded',
      details: 'Open Manage Children to retry',
    };
  }

  return (
    <div className="roadmap-summary">
      <div className="roadmap-summary-header">
        {isChildLoading ? (
          <Skeleton
            active
            avatar={{ size: 52 }}
            title={false}
            paragraph={{ rows: 2 }}
            className="roadmap-summary-child-skeleton"
          />
        ) : (
          <div className="roadmap-summary-child">
            <Avatar size={52} className="child-avatar">
              {getInitials(defaultChild)}
            </Avatar>
            <div className="roadmap-summary-child-info">
              <Text type="secondary" className="roadmap-summary-eyebrow">
                Toys picked for
              </Text>
              <div className="roadmap-summary-name-row">
                <Text strong className="roadmap-summary-name" ellipsis>
                  {childSummary?.name}
                </Text>
                {defaultChild && <Tag color="purple">Default child</Tag>}
              </div>
              <Text type="secondary">{childSummary?.details}</Text>
            </div>
          </div>
        )}

        <div className="roadmap-summary-plan">
          <span className="roadmap-summary-plan-icon">
            <CrownOutlined />
          </span>
          <div className="roadmap-summary-plan-info">
            <Text strong>{planLabel}</Text>
            {validity && (
              <Text
                type={validity?.isExpired ? 'danger' : 'secondary'}
                className="ultra-plan-caption"
              >
                {validity?.text}
              </Text>
            )}
            {weeks?.length > 0 && (
              <Text type="secondary" className="ultra-plan-caption">
                {roadmap?.isStarted && roadmap?.startedAt
                  ? `Roadmap started ${formatDate(
                      roadmap?.startedAt,
                      DATE_FORMATS?.REGULAR,
                    )}`
                  : 'Roadmap not started yet'}
              </Text>
            )}
          </div>
        </div>

        <div className="roadmap-summary-actions">
          {roadmapDocument && (
            <Button
              icon={<EyeOutlined />}
              onClick={() => openRoadmapDocument(roadmapDocument)}
            >
              Roadmap File
            </Button>
          )}
          <Button icon={<TeamOutlined />} onClick={onOpenSubscriber}>
            Manage Children
          </Button>
        </div>
      </div>

      {weeks?.length > 0 && (
        <div className="roadmap-summary-stats">
          <StatTile
            icon={<FlagOutlined />}
            label="Current week"
            value={
              currentWeekNumber ? `Week ${currentWeekNumber}` : 'Not started'
            }
          />
          <StatTile label="Status" {...roadmapStatus} />
          <StatTile
            icon={<CalendarOutlined />}
            label="Weeks planned"
            value={weeks?.length}
            suffix={planWeeks ? `/ ${planWeeks}` : undefined}
          />
          <StatTile
            icon={<AppstoreOutlined />}
            label="Toys assigned"
            value={assignedCount}
            suffix={`/ ${totalSlots}`}
          />
        </div>
      )}
    </div>
  );
};

export default RoadmapContextCard;
